import { useState, useEffect, useMemo, useCallback } from 'react';
import { BlockStack, Text, InlineStack, Badge, Button, TextField, Tabs } from '@shopify/polaris';
import { AREAS, getLineLabel, getUnitLabel } from '../../../../config/roasterySpec';
import LineTechDescEditor from './LineTechDescEditor.jsx';
import TiptapEditor from '../../../../shared/components/TiptapEditor.jsx';

const SECTION_LANGUAGE_TABS = [
  { id: 'section-en', content: 'English' },
  { id: 'section-nl', content: 'Nederlands' },
  { id: 'section-fr', content: 'Français' }
];

const SECTION_LANG_FIELDS = {
  0: 'TechnicalDescription',
  1: 'TechnicalDescriptionNL',
  2: 'TechnicalDescriptionFR'
};

function parseTechDescImages(value) {
  if (!value) return [];
  const normalizeImages = (images) =>
    images
      .filter((img) => img && typeof img === 'object' && img.key)
      .map((img) => ({
        ...img,
        displayMode: img.displayMode === 'wide' || img.layout === 'wide' || img.fullWidth === true ? 'wide' : 'standard'
      }));
  if (Array.isArray(value)) return normalizeImages(value);
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? normalizeImages(parsed) : [];
  } catch (err) {
    return [];
  }
}

function createSectionId() {
  return `section-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeSectionList(sections) {
  if (!Array.isArray(sections)) return [];

  return sections
    .map((section, index) => {
      const safe = section && typeof section === 'object' ? section : {};
      const sortOrder = Number.parseInt(safe.SortOrder, 10);
      return {
        ID: safe.ID ? String(safe.ID) : `section-${index + 1}`,
        Title: safe.Title == null ? '' : String(safe.Title),
        TechnicalDescription: safe.TechnicalDescription || null,
        TechnicalDescriptionNL: safe.TechnicalDescriptionNL || null,
        TechnicalDescriptionFR: safe.TechnicalDescriptionFR || null,
        Images: parseTechDescImages(safe.Images),
        SortOrder: Number.isInteger(sortOrder) && sortOrder > 0 ? sortOrder : index + 1
      };
    })
    .sort((a, b) => a.SortOrder - b.SortOrder)
    .map((section, index) => ({ ...section, SortOrder: index + 1 }));
}

function parseTechnicalDescriptionSections(value) {
  if (value == null || value === '') return [];
  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch (err) {
      return [];
    }
  }
  return normalizeSectionList(parsed);
}

function sectionsToStorageValue(sections) {
  return JSON.stringify(normalizeSectionList(sections));
}

// Group lines by Area -> LineNumber; every quotation line gets its own entry
function groupLines(lines, areaConfigs) {
  const groups = [];
  const seen = new Map(); // key: `${areaCode}-${lineNumber}`, value: lines[]

  const areaOrderOverrides = {};
  for (const config of areaConfigs || []) {
    if (!config || config.LineNumber != null || config.UnitNumber != null) continue;
    const parsedOrder = Number.parseInt(config.SortOrder, 10);
    if (Number.isInteger(parsedOrder)) areaOrderOverrides[config.AreaCode] = parsedOrder;
  }
  const getAreaOrder = (code) => areaOrderOverrides[code] ?? AREAS[code]?.order ?? 99;

  // Sort by area order, then line number, then unit number, then sort order
  const sorted = [...lines].sort((a, b) => {
    const areaOrder = getAreaOrder(a.AreaCode) - getAreaOrder(b.AreaCode);
    if (areaOrder !== 0) return areaOrder;
    if (a.LineNumber !== b.LineNumber) return (a.LineNumber || 0) - (b.LineNumber || 0);
    if ((a.UnitNumber || 0) !== (b.UnitNumber || 0)) return (a.UnitNumber || 0) - (b.UnitNumber || 0);
    return (a.SortOrder || 0) - (b.SortOrder || 0);
  });

  for (const line of sorted) {
    const groupKey = `${line.AreaCode}-${line.LineNumber}`;
    if (!seen.has(groupKey)) {
      seen.set(groupKey, []);
      groups.push({ areaCode: line.AreaCode, lineNumber: line.LineNumber, lines: seen.get(groupKey) });
    }
    seen.get(groupKey).push(line);
  }

  return groups;
}

function normalizeResolvedLabel(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function getLineDescription(line) {
  return String(line?.Description || line?.Product?.ProductNameNL || line?.Product?.ProductName || '').trim();
}

export default function TechDescPanel({ lines, quotation, quotationId, onRefresh, onQuotationRefresh, isLocked = false, areaConfigs }) {
  const [expandedLines, setExpandedLines] = useState(new Set());
  const [freeSections, setFreeSections] = useState([]);
  const [initialFreeSections, setInitialFreeSections] = useState([]);
  const [sectionTabById, setSectionTabById] = useState({});
  const [savingFreeSections, setSavingFreeSections] = useState(false);
  const [freeSectionsError, setFreeSectionsError] = useState('');

  useEffect(() => {
    const parsed = parseTechnicalDescriptionSections(quotation?.TechnicalDescriptionSections);
    setFreeSections(parsed);
    setInitialFreeSections(parsed);
    setSectionTabById((prev) => {
      const next = {};
      for (const section of parsed) {
        next[section.ID] = prev[section.ID] ?? 0;
      }
      return next;
    });
  }, [quotation?.TechnicalDescriptionSections]);

  const freeSectionsDirty = useMemo(() => {
    return sectionsToStorageValue(freeSections) !== sectionsToStorageValue(initialFreeSections);
  }, [freeSections, initialFreeSections]);

  const toggleLine = (lineId) => {
    setExpandedLines((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const expandAll = () => {
    const allIds = new Set(lines.map((l) => l.ID));
    setExpandedLines(allIds);
  };

  const collapseAll = () => {
    setExpandedLines(new Set());
  };

  const handleAddFreeSection = () => {
    if (isLocked) return;
    setFreeSections((prev) =>
      normalizeSectionList([
        ...prev,
        {
          ID: createSectionId(),
          Title: '',
          TechnicalDescription: null,
          TechnicalDescriptionNL: null,
          TechnicalDescriptionFR: null,
          Images: [],
          SortOrder: prev.length + 1
        }
      ])
    );
    setFreeSectionsError('');
  };

  const handleMoveFreeSection = (sectionId, direction) => {
    if (isLocked) return;
    setFreeSections((prev) => {
      const idx = prev.findIndex((section) => section.ID === sectionId);
      const targetIdx = idx + direction;
      if (idx < 0 || targetIdx < 0 || targetIdx >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(idx, 1);
      next.splice(targetIdx, 0, moved);
      return normalizeSectionList(next);
    });
    setFreeSectionsError('');
  };

  const handleDeleteFreeSection = (sectionId) => {
    if (isLocked) return;
    if (!window.confirm('Delete this free technical description section?')) return;
    setFreeSections((prev) => normalizeSectionList(prev.filter((section) => section.ID !== sectionId)));
    setSectionTabById((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
    setFreeSectionsError('');
  };

  const handleSectionChange = (sectionId, patch) => {
    if (isLocked) return;
    setFreeSections((prev) => prev.map((section) => (section.ID === sectionId ? { ...section, ...patch } : section)));
    setFreeSectionsError('');
  };

  const persistFreeSections = useCallback(
    async (sections, { skipRefresh = false } = {}) => {
      if (isLocked || !quotationId) return null;

      setSavingFreeSections(true);
      setFreeSectionsError('');
      try {
        const normalized = normalizeSectionList(sections);
        const response = await fetch(`/api/project-quotations/${quotationId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            TechnicalDescriptionSections: sectionsToStorageValue(normalized)
          })
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || `Save failed (${response.status})`);
        }

        setFreeSections(normalized);
        setInitialFreeSections(normalized);

        if (!skipRefresh) {
          if (onQuotationRefresh) {
            await onQuotationRefresh();
          } else if (onRefresh) {
            onRefresh();
          }
        }
        return normalized;
      } catch (err) {
        setFreeSectionsError(err.message || 'Failed to save free technical description sections.');
        return null;
      } finally {
        setSavingFreeSections(false);
      }
    },
    [isLocked, quotationId, onQuotationRefresh, onRefresh]
  );

  const handleSaveFreeSections = async () => {
    if (isLocked || !quotationId || !freeSectionsDirty) return;
    await persistFreeSections(freeSections);
  };

  const syncFreeSectionsFromResponse = (sections) => {
    const normalized = normalizeSectionList(sections);
    setFreeSections(normalized);
    setInitialFreeSections(normalized);
  };

  const handleSectionImageUpload = async (sectionId, file) => {
    if (isLocked || !quotationId) return;
    const savedSections = freeSectionsDirty ? await persistFreeSections(freeSections, { skipRefresh: true }) : normalizeSectionList(freeSections);
    if (!savedSections) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('sectionId', sectionId);
    setFreeSectionsError('');
    try {
      const response = await fetch(`/api/project-quotations/${quotationId}/tech-section-images`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || `Image upload failed (${response.status})`);
      syncFreeSectionsFromResponse(data.sections || []);
      if (onQuotationRefresh) await onQuotationRefresh();
    } catch (err) {
      setFreeSectionsError(err.message || 'Image upload failed. Please try again.');
    }
  };

  const handleSectionImageDelete = async (sectionId, key) => {
    if (isLocked || !quotationId) return;
    if (!window.confirm('Delete this image?')) return;
    const savedSections = freeSectionsDirty ? await persistFreeSections(freeSections, { skipRefresh: true }) : normalizeSectionList(freeSections);
    if (!savedSections) return;

    setFreeSectionsError('');
    try {
      const response = await fetch(`/api/project-quotations/${quotationId}/tech-section-images`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sectionId, key })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || `Image delete failed (${response.status})`);
      syncFreeSectionsFromResponse(data.sections || []);
      if (onQuotationRefresh) await onQuotationRefresh();
    } catch (err) {
      setFreeSectionsError(err.message || 'Image delete failed. Please try again.');
    }
  };

  const handleSectionImageUpdate = async (sectionId, key, updates) => {
    if (isLocked || !quotationId) return;
    const savedSections = freeSectionsDirty ? await persistFreeSections(freeSections, { skipRefresh: true }) : normalizeSectionList(freeSections);
    if (!savedSections) return;

    setFreeSectionsError('');
    try {
      const response = await fetch(`/api/project-quotations/${quotationId}/tech-section-images`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sectionId, key, ...updates })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || `Image update failed (${response.status})`);
      syncFreeSectionsFromResponse(data.sections || []);
      if (onQuotationRefresh) await onQuotationRefresh();
    } catch (err) {
      setFreeSectionsError(err.message || 'Image update failed. Please try again.');
    }
  };

  const getSectionImageUrl = useCallback(async (key) => `/api/project-quotations/${quotationId}/tech-section-images/view?key=${encodeURIComponent(key)}`, [quotationId]);

  // Resolve custom labels from area configs, same as the pricing table
  const getCustomLabel = (areaCode, lineNumber, unitNumber) => {
    if (!Array.isArray(areaConfigs)) return null;
    const customLabel =
      areaConfigs.find(
        (c) => c.AreaCode === areaCode && (lineNumber == null ? c.LineNumber == null : c.LineNumber === lineNumber) && (unitNumber == null ? c.UnitNumber == null : c.UnitNumber === unitNumber)
      )?.CustomLabel || null;
    const normalizedLabel = normalizeResolvedLabel(customLabel);
    if (lineNumber != null && normalizedLabel) {
      const matchesProductDescription = lines.some(
        (item) =>
          item.AreaCode === areaCode &&
          item.LineNumber === lineNumber &&
          (unitNumber == null || (item.UnitNumber || 0) === unitNumber) &&
          normalizeResolvedLabel(getLineDescription(item)) === normalizedLabel
      );
      if (matchesProductDescription) return null;
    }
    return customLabel;
  };

  const groups = groupLines(lines, areaConfigs);
  const hasAnyLineDesc = lines.some((l) => l.TechnicalDescription || l.TechnicalDescriptionNL || l.TechnicalDescriptionFR);

  return (
    <BlockStack gap="400">
      <div style={{ border: '1px solid #e1e3e5', borderRadius: '10px', padding: '12px', background: '#f9fafb' }}>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingSm" as="h3">
              Free Sections
            </Text>
            <InlineStack gap="200">
              {!isLocked && (
                <Button size="slim" onClick={handleAddFreeSection}>
                  + Add Section
                </Button>
              )}
              {freeSectionsDirty && !isLocked && (
                <Button variant="primary" size="slim" onClick={handleSaveFreeSections} loading={savingFreeSections}>
                  Save Free Sections
                </Button>
              )}
            </InlineStack>
          </InlineStack>

          <Text as="p" tone="subdued" variant="bodySm">
            Add standalone technical description sections. These are shown in the Technical Descriptions output without requiring a pricing line.
          </Text>

          {freeSectionsError && (
            <Text as="p" tone="critical" variant="bodySm">
              {freeSectionsError}
            </Text>
          )}

          {freeSections.length === 0 ? (
            <Text as="p" tone="subdued">
              No free sections yet.
            </Text>
          ) : (
            <BlockStack gap="300">
              {freeSections.map((section, index) => {
                const selectedTab = sectionTabById[section.ID] ?? 0;
                const currentField = SECTION_LANG_FIELDS[selectedTab];
                return (
                  <div key={section.ID} style={{ border: '1px solid #ddd', borderRadius: '8px', background: '#fff', padding: '12px' }}>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text variant="headingSm" as="h4">
                          Section {index + 1}
                        </Text>
                        {!isLocked && (
                          <InlineStack gap="100">
                            <Button size="slim" onClick={() => handleMoveFreeSection(section.ID, -1)} disabled={index === 0}>
                              Up
                            </Button>
                            <Button size="slim" onClick={() => handleMoveFreeSection(section.ID, 1)} disabled={index === freeSections.length - 1}>
                              Down
                            </Button>
                            <Button size="slim" tone="critical" onClick={() => handleDeleteFreeSection(section.ID)}>
                              Delete
                            </Button>
                          </InlineStack>
                        )}
                      </InlineStack>

                      <TextField label="Section title" value={section.Title || ''} onChange={(value) => handleSectionChange(section.ID, { Title: value })} autoComplete="off" disabled={isLocked} />

                      <Tabs
                        tabs={SECTION_LANGUAGE_TABS}
                        selected={selectedTab}
                        onSelect={(tabIndex) =>
                          setSectionTabById((prev) => ({
                            ...prev,
                            [section.ID]: tabIndex
                          }))
                        }
                        fitted
                      >
                        <div style={{ marginTop: '8px' }}>
                          <TiptapEditor
                            content={section[currentField]}
                            onChange={(value) => handleSectionChange(section.ID, { [currentField]: value })}
                            disabled={isLocked}
                            images={section.Images || []}
                            onImageUpload={(file) => handleSectionImageUpload(section.ID, file)}
                            onImageDelete={(key) => handleSectionImageDelete(section.ID, key)}
                            onImageUpdate={(key, updates) => handleSectionImageUpdate(section.ID, key, updates)}
                            getImageUrl={getSectionImageUrl}
                          />
                        </div>
                      </Tabs>
                    </BlockStack>
                  </div>
                );
              })}
            </BlockStack>
          )}
        </BlockStack>
      </div>

      <InlineStack align="space-between" blockAlign="center">
        <Text variant="headingSm" as="h3">
          Technical Descriptions
        </Text>
        <InlineStack gap="200">
          <Button size="slim" onClick={expandAll}>
            Expand All
          </Button>
          <Button size="slim" onClick={collapseAll}>
            Collapse All
          </Button>
        </InlineStack>
      </InlineStack>

      {lines.length === 0 ? (
        <Text as="p" tone="subdued">
          No product-linked descriptions yet. Add products in the Pricing tab if you also want line-based technical descriptions.
        </Text>
      ) : !hasAnyLineDesc ? (
        <Text as="p" tone="subdued">
          No technical descriptions yet. Click on a product below to add one. Descriptions are copied from the product master when a product is added to the quotation.
        </Text>
      ) : null}

      {groups.map((group) => {
        const areaLabel = getCustomLabel(group.areaCode, null, null) || AREAS[group.areaCode]?.label || group.areaCode;
        const lineLabel = getCustomLabel(group.areaCode, group.lineNumber, null) || getLineLabel(group.lineNumber);
        const showUnits = group.lines.some((l) => (l.UnitNumber || 0) !== 0) || new Set(group.lines.map((l) => l.UnitNumber || 0)).size > 1;

        return (
          <div key={`${group.areaCode}-${group.lineNumber}`}>
            <div style={{ padding: '6px 12px', background: '#f1f2f4', borderRadius: '6px', marginBottom: '4px' }}>
              <Text variant="headingSm" as="h4">
                {areaLabel} - {String(group.lineNumber).padStart(3, '0')} {lineLabel}
              </Text>
            </div>

            {group.lines.map((line) => {
              const isExpanded = expandedLines.has(line.ID);
              const hasDesc = line.TechnicalDescription || line.TechnicalDescriptionNL || line.TechnicalDescriptionFR;
              const hasImages = parseTechDescImages(line.TechDescImages).length > 0;
              const unitNumber = line.UnitNumber || 0;
              // Solo unit: the item description is the unit's title, so only the unit number is prefixed
              const soloUnit = group.lines.filter((l) => (l.UnitNumber || 0) === unitNumber).length === 1;
              const unitLabel = soloUnit ? '' : getCustomLabel(line.AreaCode, line.LineNumber, unitNumber) || getUnitLabel(unitNumber);

              return (
                <div key={line.ID} style={{ marginLeft: '12px', borderLeft: '2px solid #e1e3e5' }}>
                  <div
                    onClick={() => toggleLine(line.ID)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: isExpanded ? '#f9fafb' : 'transparent',
                      borderBottom: '1px solid #f1f2f4'
                    }}
                  >
                    <span style={{ userSelect: 'none', fontSize: '12px', color: '#666' }}>{isExpanded ? '\u25BC' : '\u25B6'}</span>
                    {showUnits && (
                      <Text variant="bodySm" as="span" tone="subdued">
                        {`${String(unitNumber).padStart(4, '0')}${unitLabel ? ` ${unitLabel}` : ''}`}
                      </Text>
                    )}
                    <Text variant="bodySm" as="span" fontWeight="semibold">
                      {line.Product?.Productref || '-'}
                    </Text>
                    <Text variant="bodySm" as="span">
                      {line.Description || line.Product?.ProductNameNL || 'Unnamed'}
                    </Text>
                    {hasDesc && (
                      <Badge size="small" tone="success">
                        Has description
                      </Badge>
                    )}
                    {hasImages && (
                      <Badge size="small" tone="info">
                        Images
                      </Badge>
                    )}
                    {!hasDesc && (
                      <Badge size="small" tone="attention">
                        No description
                      </Badge>
                    )}
                  </div>
                  {isExpanded && <LineTechDescEditor line={line} quotationId={quotationId} onRefresh={onRefresh} disabled={isLocked} />}
                </div>
              );
            })}
          </div>
        );
      })}
    </BlockStack>
  );
}
